// feature/one-donations-page, May 15th 2026: One-time donation checkout wizard

import WizardController from "./wizard_controller"
import { paymentChallengeToken, resetPaymentChallenge, waitForPaymentChallenge } from "../utils/payment_security"

export default class extends WizardController {
  static targets = [
    "step", "progressBarColumn", "currencySelect", "managedPortfolioSelect",
    "amountInput", "amountCentsHidden", "amountCurrencySymbol",
    "paymentElement", "paymentErrors", "paymentProcessorMethodId", "paymentProcessorMethodType", "paymentIntentId",
    "firstName", "lastName", "email",
    "previous", "progress", "submitButton",
    "step0Actions", "step1Actions",
    "required_field_0", "required_field_1", "email_field_0"
  ]
  static values = {
    stripeApiKey: String,
    minimumAmountCents: Number,
    portfoliosOptionsByPartnerId: Object,
    paymentIntentUrl: String
  }

  connect() {
    super.connect()
    this.stripe = null
    this.elements = null
    this.clientSecret = null
    this.isSubmitting = false
    this.index = 0
    this.showStep(0)
    this.syncCurrencyFromPartnerSelect()
    this.refreshPortfolioOptions()
  }

  currencySelected() {
    this.syncCurrencyFromPartnerSelect()
    this.refreshPortfolioOptions()
  }

  syncCurrencyFromPartnerSelect() {
    const select = this.currencySelectTarget
    const option = select?.options[select?.selectedIndex]
    const text = option?.text || ''
    const currencyMatch = text.match(/\s([A-Z]{3})$/)
    const currency = currencyMatch ? currencyMatch[1] : 'USD'
    const symbol = { GBP: '£', USD: '$', CAD: '$', AUD: '$', EUR: '€' }[currency] || '$'
    if (this.hasAmountCurrencySymbolTarget) this.amountCurrencySymbolTarget.textContent = symbol
  }

  refreshPortfolioOptions() {
    if (!this.hasManagedPortfolioSelectTarget) return
    const partnerId = this.currencySelectTarget?.value?.toString()
    const options = this.portfoliosOptionsByPartnerIdValue?.[partnerId] || []
    const select = this.managedPortfolioSelectTarget
    const previousValue = select.value

    select.innerHTML = ''
    options.forEach((opt) => {
      const option = document.createElement('option')
      option.value = String(opt.id)
      option.textContent = opt.name
      select.appendChild(option)
    })

    const hasPrevious = options.some((opt) => String(opt.id) === String(previousValue))
    if (hasPrevious) {
      select.value = previousValue
    } else if (options.length > 0) {
      select.value = String(options[0].id)
    } else {
      select.value = ''
    }
  }

  async next(event) {
    event?.preventDefault()
    this.syncAmountCents()
    this.validateFields()
    if (!this.valid) {
      const minCents = this.minimumAmountCentsValue || 500
      const symbol = this.hasAmountCurrencySymbolTarget ? this.amountCurrencySymbolTarget.textContent : '$'
      this.showError(`Please complete all required fields. Amount must be at least ${symbol}${minCents / 100}.`)
      return
    }
    if (this.hasProgressTarget) this.progressTarget.disabled = true
    this.clearError()
    this.showStep(1)
    try {
      await waitForPaymentChallenge(this.element)
      const error = await this.replacePaymentIntent()
      if (error) {
        this.showError(error)
        return
      }
    } catch (err) {
      console.error('One-time donation nextStep error:', err)
      this.showError(err.message || 'Something went wrong. Please try again.')
      return
    } finally {
      if (this.hasProgressTarget) this.progressTarget.disabled = false
    }
  }

  previous(event) {
    event?.preventDefault()
    this.resetPaymentStep()
    this.showStep(0)
  }

  showStep(index) {
    this.index = index
    this.stepTargets.forEach((el, i) => {
      el.classList.toggle('is-hidden', index !== i)
    })
    if (this.hasProgressBarColumnTarget) {
      this.progressBarColumnTargets.forEach((el, i) => {
        el.classList.toggle('blue', i <= index)
      })
    }
    this.updateNavigation()
  }

  updateNavigation() {
    const isStep0 = this.index === 0
    if (this.hasStep0ActionsTarget) this.step0ActionsTarget.classList.toggle('is-hidden', !isStep0)
    if (this.hasStep1ActionsTarget) this.step1ActionsTarget.classList.toggle('is-hidden', isStep0)
    const canSubmit = !!this.clientSecret && !!this.elements
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = !canSubmit
  }

  resetPaymentStep() {
    this.clientSecret = null
    if (this.elements && this.hasPaymentElementTarget) {
      this.paymentElementTarget.innerHTML = ''
    }
    this.elements = null
    this.stripe = null
    if (this.hasPaymentIntentIdTarget) this.paymentIntentIdTarget.value = ''
    if (this.hasPaymentProcessorMethodIdTarget) this.paymentProcessorMethodIdTarget.value = ''
    if (this.hasPaymentProcessorMethodTypeTarget) this.paymentProcessorMethodTypeTarget.value = ''
  }

  syncAmountCents() {
    if (!this.hasAmountCentsHiddenTarget) return
    const input = this.amountInputTarget
    const num = parseFloat(input?.value)
    const cents = Number.isNaN(num) ? 0 : Math.round(num * 100)
    const min = this.minimumAmountCentsValue || 500
    this.amountCentsHiddenTarget.value = cents >= min ? cents : ''
  }

  async replacePaymentIntent() {
    const data = await this.createPaymentIntent()
    if (data.error || !data.client_secret) {
      return data.error || 'Could not start payment. Please try again.'
    }
    this.clientSecret = data.client_secret
    if (this.hasPaymentIntentIdTarget) this.paymentIntentIdTarget.value = data.payment_intent_id
    this.mountPaymentElement(data.client_secret, data.stripe_account_id)
    this.updateNavigation()
    return null
  }

  // The charge.failed webhook cancels this PaymentIntent so the client_secret
  // cannot be reused with another card. Mint a replacement so a real donor who
  // mistyped CVC can retry without going back a step.
  async refreshPaymentIntentAfterFailure() {
    try {
      await waitForPaymentChallenge(this.element)
      const error = await this.replacePaymentIntent()
      if (error) {
        this.resetPaymentStep()
        this.updateNavigation()
      }
    } catch (err) {
      console.error('One-time donation replacePaymentIntent error:', err)
      this.resetPaymentStep()
      this.updateNavigation()
    }
  }

  async createPaymentIntent() {
    const response = await fetch(this.paymentIntentUrlValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
      },
      // feature/payment-guardrails: sent so the webhook can reconstruct the donor and contribution
      // if this browser never makes it back to submit the full form (closed tab, crash, network
      // drop after Stripe confirms payment) -- see StripeController#orphan_recovery_metadata.
      body: JSON.stringify({
        partner_id: this.currencySelectTarget?.value,
        amount_cents: parseInt(this.amountCentsHiddenTarget.value, 10),
        payment_challenge_token: paymentChallengeToken(this.element),
        email: this.hasEmailTarget ? this.emailTarget.value?.trim() : undefined,
        first_name: this.hasFirstNameTarget ? this.firstNameTarget.value?.trim() : undefined,
        last_name: this.hasLastNameTarget ? this.lastNameTarget.value?.trim() : undefined,
        managed_portfolio_id: this.hasManagedPortfolioSelectTarget ? this.managedPortfolioSelectTarget.value : undefined
      })
    })
    resetPaymentChallenge(this.element)
    const text = await response.text()
    try {
      return JSON.parse(text)
    } catch {
      throw new Error(response.ok ? 'Invalid response from server' : `Request failed (${response.status}). Please try again.`)
    }
  }

  mountPaymentElement(clientSecret, stripeAccountId) {
    const stripeOptions = stripeAccountId ? { stripeAccount: stripeAccountId } : {}
    this.stripe = window.Stripe(this.stripeApiKeyValue, stripeOptions)
    this.elements = this.stripe.elements({
      clientSecret,
      appearance: {
        theme: 'stripe',
        variables: {
          colorPrimary: '#2675F8',
          colorBackground: '#ffffff',
          colorText: '#30313d',
          colorDanger: '#df1b41',
          fontFamily: 'system-ui, sans-serif',
          spacingUnit: '4px',
          borderRadius: '8px'
        }
      }
    })
    const paymentElement = this.elements.create('payment', {
      fields: { billingDetails: 'auto' },
      layout: { type: 'accordion', defaultCollapsed: false }
    })
    requestAnimationFrame(() => {
      if (this.hasPaymentElementTarget) {
        this.paymentElementTarget.innerHTML = ''
        paymentElement.mount(this.paymentElementTarget)
      }
    })
  }

  async submitPayment(event) {
    event?.preventDefault()
    if (this.isSubmitting || !this.elements || !this.clientSecret) return

    this.clearError()
    const submitResult = await this.elements.submit()
    if (submitResult?.error) {
      this.showError(submitResult.error?.message || 'Please complete all required fields.')
      return
    }

    this.isSubmitting = true
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = true
    try {
      const firstName = this.hasFirstNameTarget ? this.firstNameTarget.value?.trim() : ''
      const lastName = this.hasLastNameTarget ? this.lastNameTarget.value?.trim() : ''
      const email = this.hasEmailTarget ? this.emailTarget.value?.trim() : ''
      const fullName = [firstName, lastName].filter(Boolean).join(' ')
      const confirmParams = {
        return_url: window.location.href,
        receipt_email: email || undefined
      }
      if (fullName || email) {
        confirmParams.payment_method_data = {
          billing_details: {
            ...(fullName && { name: fullName }),
            ...(email && { email })
          }
        }
      }
      const result = await this.stripe.confirmPayment({
        elements: this.elements,
        confirmParams,
        redirect: 'if_required'
      })
      if (result?.error) {
        this.showError(result.error.message || 'Payment failed.')
        await this.refreshPaymentIntentAfterFailure()
        return
      }
      const status = result.paymentIntent?.status
      if (status === 'succeeded' || status === 'processing') {
        if (this.hasPaymentProcessorMethodIdTarget) this.paymentProcessorMethodIdTarget.value = result.paymentIntent.payment_method || ''
        if (this.hasPaymentProcessorMethodTypeTarget) this.paymentProcessorMethodTypeTarget.value = result.paymentIntent.payment_method_types?.[0] || 'card'
        this.element.submit()
      } else {
        this.showError('Payment was not completed. Please try again.')
        await this.refreshPaymentIntentAfterFailure()
      }
    } catch (err) {
      console.error('One-time donation submitPayment error:', err)
      this.showError(err.message || 'Payment failed. Please try again.')
    } finally {
      this.isSubmitting = false
      this.updateNavigation()
    }
  }

  showError(message) {
    if (!this.hasPaymentErrorsTarget) return
    this.paymentErrorsTarget.textContent = message
    this.paymentErrorsTarget.classList.remove('is-hidden')
  }

  clearError() {
    if (!this.hasPaymentErrorsTarget) return
    this.paymentErrorsTarget.textContent = ''
    this.paymentErrorsTarget.classList.add('is-hidden')
  }

  customValidations() {
    if (this.index === 1 && !this.paymentIntentIdTarget?.value) {
      this.showError('Please complete the payment before continuing.')
      return false
    }
    return true
  }
}
